import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useListAdminInvestorsQuery } from '../../features/investors/adminInvestorsApi';
import Card, { CardHeader } from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import { MobileCard, DataRow } from '../../components/ui/MobileCard';
import { SkeletonTable } from '../../components/ui/Skeleton';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'pending_approval', label: 'Pending approval' },
  { value: 'active', label: 'Active' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'suspended', label: 'Suspended' },
];

const STATUS_TONE = { pending_approval: 'warning', active: 'success', rejected: 'danger', suspended: 'neutral' };

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

/**
 * GET /admin/investors?status=&search=&per_page=&page= — gated by
 * manage-investors, a permission distinct from manage-investments (this
 * screen only browses investors; creating an investment for one of them
 * is gated separately on the detail screen).
 */
export default function InvestorsList() {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  // Debounce the search box so every keystroke doesn't fire a request.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading, isFetching, isError, error } = useListAdminInvestorsQuery({ status: status || undefined, search: search || undefined, page });

  const investors = data?.investors ?? [];
  const meta = data?.meta;
  const parseError = data?.parseError;

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-semibold text-foreground">Investors</h1>
          <Badge tone="success">Live</Badge>
        </div>
        <p className="text-foreground-muted text-sm mt-1">Browse and search all investor accounts.</p>
      </div>

      <Card>
        <CardHeader title="All investors" subtitle={isFetching && !isLoading ? 'Refreshing…' : undefined} />

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="flex-1">
            <Input
              placeholder="Search by name or email…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            className="rounded-xl bg-background-elevated border border-border px-3.5 py-2.5 text-sm text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {isLoading && <SkeletonTable columns={['Name', 'Email', 'Phone', 'Status', 'Last login']} />}

        {isError && (
          <p className="py-8 text-center text-danger">Could not load investors. Check your connection and try again.</p>
        )}

        {!isLoading && !isError && parseError && (
          <p className="py-8 text-center text-danger">
            Something went wrong loading this data. Check the console for details.
          </p>
        )}

        {!isLoading && !isError && !parseError && investors.length === 0 && (
          <p className="py-8 text-center text-foreground-muted">No investors match this search.</p>
        )}

        {!isLoading && !isError && !parseError && investors.length > 0 && (
          <>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-foreground-subtle border-b border-border">
                    <th className="py-2 pr-4 font-medium">Name</th>
                    <th className="py-2 pr-4 font-medium">Email</th>
                    <th className="py-2 pr-4 font-medium">Phone</th>
                    <th className="py-2 pr-4 font-medium">Status</th>
                    <th className="py-2 font-medium">Last login</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {investors.map((inv) => (
                    <tr
                      key={inv.id}
                      className="cursor-pointer hover:bg-background-elevated transition-colors"
                      onClick={() => navigate(`/admin/investors/${inv.id}`)}
                    >
                      <td className="py-3 pr-4 text-foreground">{inv.full_name}</td>
                      <td className="py-3 pr-4 text-foreground-muted">{inv.email}</td>
                      <td className="py-3 pr-4 text-foreground-muted">{inv.phone || '—'}</td>
                      <td className="py-3 pr-4">
                        <Badge tone={STATUS_TONE[inv.account_status] || 'neutral'}>
                          {(inv.account_status || 'unknown').replace('_', ' ').toUpperCase()}
                        </Badge>
                      </td>
                      <td className="py-3 text-foreground-muted">{formatDate(inv.last_login_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="md:hidden space-y-3">
              {investors.map((inv) => (
                <MobileCard key={inv.id} className="cursor-pointer" onClick={() => navigate(`/admin/investors/${inv.id}`)}>
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-medium text-foreground">{inv.full_name}</p>
                    <Badge tone={STATUS_TONE[inv.account_status] || 'neutral'}>
                      {(inv.account_status || 'unknown').replace('_', ' ').toUpperCase()}
                    </Badge>
                  </div>
                  <DataRow label="Email" value={inv.email} />
                  <DataRow label="Phone" value={inv.phone || '—'} />
                  <DataRow label="Last login" value={formatDate(inv.last_login_at)} />
                </MobileCard>
              ))}
            </div>

            {meta && meta.lastPage > 1 && (
              <div className="flex items-center justify-between pt-4 mt-4 border-t border-border">
                <p className="text-xs text-foreground-subtle">
                  Page {meta.currentPage} of {meta.lastPage} · {meta.total} total
                </p>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" disabled={meta.currentPage <= 1} onClick={() => setPage(Math.max(1, meta.currentPage - 1))}>
                    Previous
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={meta.currentPage >= meta.lastPage}
                    onClick={() => setPage(Math.min(meta.lastPage, meta.currentPage + 1))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
